import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Star, Loader2 } from "lucide-react";
import { uploadImage, resolveImg } from "@/lib/api";

// value: string[] of image urls/paths. First item is the main image.
export function ImageUploader({ value = [], onChange, multiple = true, label = "Obrázky" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    const list = Array.from(files);
    if (list.length === 0) return;
    setUploading(true);
    const uploaded = [];
    for (const f of list) {
      if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
        toast.error(`Nepodporovaný formát: ${f.name}`);
        continue;
      }
      if (f.size > 8 * 1024 * 1024) {
        toast.error(`Súbor je priveľký (max 8 MB): ${f.name}`);
        continue;
      }
      try {
        const res = await uploadImage(f);
        uploaded.push(res.url);
      } catch (e) {
        toast.error(e.response?.data?.detail || `Nahrávanie zlyhalo: ${f.name}`);
      }
      if (!multiple) break;
    }
    if (uploaded.length) {
      onChange(multiple ? [...value, ...uploaded] : [uploaded[0]]);
      toast.success(`Nahraté: ${uploaded.length} obrázok/obrázky`);
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));
  const makeMain = (idx) => {
    if (idx === 0) return;
    const next = [...value];
    const [item] = next.splice(idx, 1);
    next.unshift(item);
    onChange(next);
  };

  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</label>
      <div className="flex flex-wrap gap-3">
        {value.map((url, i) => (
          <div key={i} className="group relative h-24 w-24 overflow-hidden rounded-lg border border-white/10 bg-vault-black/60" data-testid={`uploader-thumb-${i}`}>
            <img src={resolveImg(url)} alt="" className="h-full w-full object-contain p-1.5" />
            {i === 0 && <span className="absolute left-1 top-1 rounded bg-violet-glow/80 px-1.5 py-0.5 text-[0.55rem] font-bold text-white">HLAVNÝ</span>}
            <div className="absolute inset-0 flex items-center justify-center gap-1 bg-vault-black/70 opacity-0 transition-opacity group-hover:opacity-100">
              {multiple && i !== 0 && (
                <button type="button" onClick={() => makeMain(i)} title="Nastaviť ako hlavný" data-testid={`uploader-main-${i}`} className="flex h-7 w-7 items-center justify-center rounded-full bg-white/10 text-cyan-300 hover:bg-white/20"><Star className="h-3.5 w-3.5" /></button>
              )}
              <button type="button" onClick={() => remove(i)} title="Odstrániť" data-testid={`uploader-remove-${i}`} className="flex h-7 w-7 items-center justify-center rounded-full bg-white/10 text-rose-300 hover:bg-white/20"><X className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="uploader-add-button"
          className="flex h-24 w-24 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-white/15 text-slate-400 transition-colors hover:border-cyan-glow/50 hover:text-cyan-300 disabled:opacity-50"
        >
          {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
          <span className="text-[0.65rem]">{uploading ? "Nahrávam…" : "Nahrať"}</span>
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple={multiple} onChange={(e) => handleFiles(e.target.files)} className="hidden" data-testid="uploader-file-input" />
      <p className="mt-2 text-xs text-slate-500">JPEG, PNG alebo WebP · max 8 MB. Prvý obrázok je hlavný.</p>
    </div>
  );
}

export default ImageUploader;
